import React, { useContext, useState } from 'react';
import cn from 'classnames';
import { useHistory } from 'react-router';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useSignOut } from 'Shared/utils/hooks/use-sign-out';

// Constants
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';

// Utils
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Context
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import { WpAvatar } from 'Wpds/components/avatar';
import { WpTooltipOverlay, useTooltip } from 'Wpds/components/tooltip';
import { WpLink } from 'Wpds/components/link';

// Styles
import './SettingsMenuItem.scss';

type Props = {
  handleCloseNavbar: Function;
};

const SettingsMenuItem = ({ handleCloseNavbar }: Props) => {
  const history = useHistory();
  const isExpanded = useContext(ExpandNavbarContext);

  const { user, isManager, isOrgAdmin } = useCurrentUser();
  const { handleSignOut: handleSignOutClick } = useSignOut();

  const tooltip = useTooltip({ placement: 'right' });

  const { first_name: firstName, last_name: lastName, avatar, email } = user;

  const [isOpen, setIsOpen] = useState(false);

  const handleCloseSidebarFn = () => {
    if (isExpanded) handleCloseNavbar();
  };

  const handleAccountSettingsClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.SETTINGS_PROFILE_PAGE);
  };

  const handleIntegrationsClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.INTEGRATIONS);
  };

  const handleAssistantsClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.ASSISTANTS_PAGE);
  };

  const handleManagerDashboardClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.ANALYTICS_MANAGER_DASHBOARD);
  };

  const handleAdminConsoleClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.SETTINGS_USER_MANAGEMENT_PAGE);
  };

  const handleSupportArticlesClick = () => {
    // TODO: PER-260
    handleLaunchExternalLink({
      href: ExternalLinksConstants.perform_support_article,
    });
  };

  const handleViewProfileClick = () => {
    handleCloseSidebarFn();
    setIsOpen(false);
  };

  const baseClassName = 'SettingsMenuItem';

  return (
    <>
      <WpDropdown placement="right-start" isOpen={isOpen} setOpen={setIsOpen}>
        <WpDropdownToggle>
          <li className="Notifications nav-item" ref={tooltip.targetRef}>
            <div className={cn('nav-link d-flex align-items-center cursor-pointer', { active: isOpen })}>
              <div
                className={cn('side-navigation-icon position-relative', {
                  'mx-auto': !isExpanded,
                })}
              >
                <WpIcon name="cog" />
              </div>
              {isExpanded && <div className="d-inline">Settings</div>}
            </div>
          </li>
        </WpDropdownToggle>
        <WpDropdownMenu fitHeight>
          <li className={`${baseClassName}__account`}>
            <div className={`${baseClassName}__account__avatar`}>
              <WpAvatar avatar={avatar} size="xl" />
            </div>
            <div className={`${baseClassName}__account__details`}>
              <p className={`${baseClassName}__account__details__name`}>
                {firstName}&nbsp;{lastName.charAt(0)}
              </p>
              <p className={`${baseClassName}__account__details__email`}>{email}</p>
              <WpLink
                href={PageUrlsConstants.CURRENT_USER_PROFILE_PAGE_HOME}
                onClick={handleViewProfileClick}
                className={`${baseClassName}__account__details__profile-link`}
              >
                View Profile
                <WpIcon name="angle-right" />
              </WpLink>
            </div>
          </li>
          <WpDropdownMenuDivider />
          <WpDropdownMenuItem onClick={handleAccountSettingsClick}>
            <WpIcon name="cog" />
            Account Settings
          </WpDropdownMenuItem>
          <WpDropdownMenuItem onClick={handleIntegrationsClick}>
            <WpIcon name="plug" />
            Integrations
          </WpDropdownMenuItem>
          {isManager && (
            <WpDropdownMenuItem onClick={handleAssistantsClick}>
              <WpIcon name="robot" />
              Assistants
            </WpDropdownMenuItem>
          )}
          {isManager && (
            <WpDropdownMenuItem onClick={handleManagerDashboardClick}>
              <WpIcon name="analytics" />
              Manager Dashboard
            </WpDropdownMenuItem>
          )}
          {isOrgAdmin && (
            <WpDropdownMenuItem onClick={handleAdminConsoleClick}>
              <WpIcon name="wrench" />
              Admin Console
            </WpDropdownMenuItem>
          )}
          <WpDropdownMenuDivider />
          <WpDropdownMenuItem onClick={handleSupportArticlesClick}>
            <WpIcon name="life-ring" />
            Support Articles & FAQ
          </WpDropdownMenuItem>
          <WpDropdownMenuItem onClick={handleSignOutClick}>
            <WpIcon name="sign-out-alt" />
            Sign Out
          </WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
      {!isExpanded && <WpTooltipOverlay context={tooltip}>Settings</WpTooltipOverlay>}
    </>
  );
};

export default SettingsMenuItem;
